import { CSSProperties, FormEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

interface WorkspaceTabDefinition {
  id: string;
  name: string;
}

interface WorkspaceTabsProps {
  workspaces: WorkspaceTabDefinition[];
  activeWorkspaceId: string;
  onSelect: (workspaceId: string) => void;
  onAdd: () => void;
  onRename: (workspaceId: string, name: string) => void;
  onRemove: (workspaceId: string) => void;
  canRemoveWorkspace: boolean;
}

function normalizeName(name: string, fallback: string): string {
  const trimmed = name.trim();
  if (!trimmed) {
    return fallback;
  }
  return trimmed.slice(0, 64);
}

const MAX_TAB_WIDTH = 288; // 18rem
const MIN_TAB_WIDTH = 200; // 7rem
const TAB_GAP = 12;

export function WorkspaceTabs({
  workspaces,
  activeWorkspaceId,
  onSelect,
  onAdd,
  onRename,
  onRemove,
  canRemoveWorkspace,
}: WorkspaceTabsProps) {
  const tabCount = workspaces.length;
  const tabListRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const [editingWorkspaceId, setEditingWorkspaceId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !tabListRef.current || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const boxSize = Array.isArray(entry.borderBoxSize) ? entry.borderBoxSize[0] : entry.borderBoxSize;
      const width = boxSize ? boxSize.inlineSize : entry.contentRect.width;
      setContainerWidth(width);
    });
    observer.observe(tabListRef.current);
    return () => observer.disconnect();
  }, []);

  const targetWidth = (() => {
    if (!containerWidth || tabCount === 0) {
      return MAX_TAB_WIDTH;
    }
    const totalGap = Math.max(0, tabCount - 1) * TAB_GAP;
    const perTab = (containerWidth - totalGap) / tabCount;
    return Math.min(MAX_TAB_WIDTH, Math.max(MIN_TAB_WIDTH, perTab));
  })();

  const tabListStyle = {
    '--workspace-tab-width': `${targetWidth}px`,
    '--workspace-tab-min-width': `${Math.min(targetWidth, MIN_TAB_WIDTH)}px`,
  } as CSSProperties;

  const startEditing = useCallback((workspaceId: string, currentName: string) => {
    setEditingWorkspaceId(workspaceId);
    setEditingName(currentName);
    setOriginalName(currentName);
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingWorkspaceId(null);
    setEditingName('');
    setOriginalName('');
  }, []);

  const commitEditing = useCallback(() => {
    if (!editingWorkspaceId) {
      return;
    }
    const normalized = normalizeName(editingName, originalName);
    if (normalized !== originalName) {
      onRename(editingWorkspaceId, normalized);
    }
    cancelEditing();
  }, [cancelEditing, editingName, editingWorkspaceId, onRename, originalName]);

  useEffect(() => {
    if (editingWorkspaceId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingWorkspaceId]);

  const handleEditorKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelEditing();
      }
    },
    [cancelEditing],
  );

  const handleEditorBlur = useCallback(() => {
    cancelEditing();
  }, [cancelEditing]);

  const handleEditorSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      commitEditing();
    },
    [commitEditing],
  );

  return (
    <div className="workspace-tabs">
      <div className="workspace-tab-list" ref={tabListRef} style={tabListStyle}>
        {workspaces.map(({ id, name }) => {
          const isActive = id === activeWorkspaceId;
          const isEditing = id === editingWorkspaceId;
          const className = `workspace-tab${isActive ? ' is-active' : ''}${isEditing ? ' is-editing' : ''}`;

          return (
            <div key={id} className={className}>
              {isEditing ? (
                <form className="workspace-tab-trigger workspace-tab-editor" onSubmit={handleEditorSubmit}>
                  <input
                    ref={inputRef}
                    className="workspace-tab-input"
                    value={editingName}
                    onChange={(event) => setEditingName(event.target.value)}
                    onKeyDown={handleEditorKeyDown}
                    onBlur={handleEditorBlur}
                    aria-label="Workspace name"
                  />
                  {canRemoveWorkspace && workspaces.length > 1 && (
                    <button
                      type="button"
                      className="workspace-tab-close"
                      onClick={(event) => {
                        event.stopPropagation();
                        cancelEditing();
                        onRemove(id);
                      }}
                      title="Close workspace"
                    >
                      ✕
                    </button>
                  )}
                </form>
              ) : (
                <div
                  className="workspace-tab-trigger"
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(id)}
                  onDoubleClick={() => startEditing(id, name)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
                      event.preventDefault();
                      onSelect(id);
                    }
                  }}
                  title={name}
                >
                  <span className="workspace-tab-label">{name}</span>
                  {canRemoveWorkspace && workspaces.length > 1 && (
                    <button
                      type="button"
                      className="workspace-tab-close"
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemove(id);
                      }}
                      title="Close workspace"
                    >
                      ✕
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button type="button" className="workspace-tab-add btn" onClick={onAdd} title="Add workspace">
        +
      </button>
    </div>
  );
}
